import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import useApi from '../Api'
import '../styles/pages/GuideDetailPage.css'
import { useAuth } from '../AuthContext'
import useGuideInteraction from '../components/guide/useGuideInteraction'
import { toast } from 'react-toastify'
import ErrorSplash from '../components/common/ErrorSplash'
import GuideFullLayout from '../components/guide/GuideFullLayout'
import useReadingTracker from '../components/common/useReadingTracker'

const GuideDetailPage = () => {
  const { origin_id, guide_id } = useParams()
  const navigate = useNavigate()
  const apiClient = useApi()
  const { token, user } = useAuth()
  const [guide, setGuide] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hasViewed, setHasViewed] = useState(false)
  const [contentsVisible, setContentsVisible] = useState(true)
  const [activeSection, setActiveSection] = useState(null)
  const { userInteraction, interactionLoading, likeGuide, dislikeGuide } = useGuideInteraction(guide_id, guide)

  useEffect(() => {
    let isMounted = true
    const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    if (!guide_id || !uuidV4Regex.test(guide_id)) {
      if (isMounted) {
        setError('This guide could not be found.')
        setLoading(false)
      }
      return
    }
    setLoading(true)
    const fetchGuide = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const response = await apiClient.get(`/guides/${guide_id}`, { headers })
        if (isMounted) {
          setGuide(response.data)
        }
      } catch {
        if (isMounted) {
          setError('This guide could not be found.')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }
    fetchGuide()
    return () => {
      isMounted = false
    }
  }, [guide_id, apiClient, token])

  useEffect(() => {
    let isMounted = true
    if (guide && !hasViewed) {
      const incrementViewCount = async () => {
        try {
          const headers = token ? { Authorization: `Bearer ${token}` } : {}
          await apiClient.post(`/guides/${guide_id}/view`, {}, { headers })
        } catch {}
      }
      incrementViewCount()
      if (isMounted) {
        setHasViewed(true)
      }
    }
    return () => {
      isMounted = false
    }
  }, [guide, guide_id, apiClient, token, hasViewed])

  useEffect(() => {
    if (!guide) return
    const navbarHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--navbar-height'), 10) || 80
    const handleScroll = () => {
      const sections = document.querySelectorAll('#introduction, [id^="day-"]')
      if (!sections.length) return
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1) {
        setActiveSection(sections[sections.length - 1].id)
        return
      }
      let closestSection = null
      let minDistance = Infinity
      sections.forEach(section => {
        const rect = section.getBoundingClientRect()
        const distance = Math.abs(rect.top - navbarHeight)
        if (distance < minDistance) {
          minDistance = distance
          closestSection = section
        }
      })
      if (closestSection) {
        setActiveSection(closestSection.id)
      }
    }
    window.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [guide])

  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })
    }
  }, [error])

  const handleLike = async () => {
    try {
      await likeGuide()
    } catch {}
  }

  const handleDislike = async () => {
    try {
      await dislikeGuide()
    } catch {}
  }

  const handleGuideContentsClick = (e, targetId) => {
    e.preventDefault()
    const targetElement = document.getElementById(targetId)
    if (targetElement) {
      const navbarHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--navbar-height'), 10) || 80
      const elementPosition = targetElement.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition - navbarHeight
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    }
  }

  useReadingTracker(guide_id, 'guide', user ? user.uid : null)

  if (loading) {
    return (
      <div className="guide-detail-page">
        <div className="loading" role="status" aria-live="polite">Loading guide...</div>
      </div>
    )
  }
  if (error || !guide) {
    return <ErrorSplash message="This guide could not be found." />
  }
  const isAuthor = user && user.uid === guide.author_id

  return (
    <>
      <Helmet>
        <title>{guide.title} - Guide Detail | WeiFinders</title>
        <meta name="description" content={guide.introduction ? guide.introduction.substring(0, 160) : 'Guide details on WeiFinders.'} />
        <meta property="og:title" content={`${guide.title} - Guide Detail | WeiFinders`} />
        <meta property="og:description" content={guide.introduction ? guide.introduction.substring(0, 160) : 'Guide details on WeiFinders.'} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`https://weifinders.com/guide/${origin_id}/${guide_id}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@WeiFinders" />
        <meta name="twitter:title" content={`${guide.title} - Guide Detail | WeiFinders`} />
        <meta name="twitter:description" content={guide.introduction ? guide.introduction.substring(0, 160) : 'Guide details on WeiFinders.'} />
        <link rel="prefetch" href="/static/js/GuideFullLayout.chunk.js" as="script" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "${guide.title}",
            "description": "${guide.introduction ? guide.introduction.substring(0, 160) : 'Guide details on WeiFinders.'}",
            "author": {
              "@type": "Person",
              "name": "${guide.author_name || 'Unknown Author'}"
            },
            "datePublished": "${guide.created_datetime_utc || ''}",
            "url": "https://weifinders.com/guide/${origin_id}/${guide_id}"
          }
        `}</script>
      </Helmet>
      <main role="main" className="guide-detail-page">
        <div className="guide-full-layout-container">
          <GuideFullLayout
            guide={guide}
            user={user}
            userInteraction={userInteraction}
            interactionLoading={interactionLoading}
            handleLike={handleLike}
            handleDislike={handleDislike}
            activeSection={activeSection}
            handleGuideContentsClick={handleGuideContentsClick}
            contentsVisible={contentsVisible}
            setContentsVisible={setContentsVisible}
            showContentsSidebar
            isAuthor={isAuthor}
            handleEdit={() => navigate(`/guide/${encodeURIComponent(origin_id)}/edit/${guide_id}`)}
          />
        </div>
      </main>
    </>
  )
}

export default GuideDetailPage
